import { PlantDto, UrlKeyDto } from '@trucktrax/trucktrax-ts-common';

import { EMAIL_NOTIFICATIONS_PATH } from '../constants/apiConstants';
import { EmailNotificationSubscriptionDto, EmailNotificationType } from '../types';
import { getGeotraxBaseUrl } from '../util/apiUtil';
import { normalizeEmail } from '../util/emailNotificationUtil';
import recordLog, { LogLevel } from '../util/errorUtil';
import { getPlantList } from './plantsService';
import { deleteRequest, getRequest, postRequest, putRequest } from './requestService';

/**
 * The Email Notifications contract (DEV-4350/DEV-4351): one row per recipient +
 * notification type + plant, served from `/notifications/email`.
 *
 * Backs both the admin screen and the user detail view's subscriptions card.
 *
 * `Size: 0` disables pagination in the drivers microservice, the same way
 * NotificationDispatchService reads the list. The admin screen groups rows by
 * address, which a page of rows cannot be grouped correctly from -- one address's
 * rows can straddle a page boundary -- so every read here is unpaginated.
 *
 * Every read also asks for live rows only. A DELETE is soft -- it sets `Deleted` -- and
 * the API applies no deleted filter unless one is sent, so without `IsDeleted: false`
 * a removed subscription comes straight back on the next read (DEV-5347).
 */

const subscriptionsBaseUrl = () => getGeotraxBaseUrl() + EMAIL_NOTIFICATIONS_PATH;

const UNPAGINATED_LIVE_ROWS = { Size: 0, IsDeleted: false };

/**
 * The topics this screen administers. `NotificationTopicType` also carries
 * `Checklists`, which is no longer supported here: nothing in the UI can create one,
 * and a row still stored against it has no plant to name and no field to edit it in,
 * so it would render as a blank scope an admin could not act on. Filtering on read
 * keeps every consumer -- the table, its expanded rows, the profile card, the save
 * diff -- from having to know about a type it cannot handle.
 */
const SUPPORTED_NOTIFICATION_TYPES: string[] = [
  EmailNotificationType.IncomingMaterials,
  EmailNotificationType.Diversions,
];

const supported = (items: EmailNotificationSubscriptionDto[]) =>
  items.filter(item => SUPPORTED_NOTIFICATION_TYPES.includes(item.notificationType));

/**
 * Case-insensitive, whitespace-tolerant address comparison, on the same terms the
 * grouping uses -- `normalizeEmail` is the one definition of what makes two addresses
 * the same, so this cannot drift from how the table collapses rows.
 */
const sameEmail = (a?: string | null, b?: string | null) => normalizeEmail(a) === normalizeEmail(b);

/**
 * Every subscription the caller may see, across every region.
 *
 * Deliberately unfiltered by region: an address is configured against plants in
 * whichever regions the admin can reach, so a region-filtered read would split one
 * address's configuration across region selections and show it as several
 * half-populated rows.
 *
 * Swallows failures to an empty list, matching the other admin table reads: the
 * screen renders its own empty state rather than blanking on a 403.
 */
export async function getAllSubscriptions(): Promise<EmailNotificationSubscriptionDto[]> {
  const url = subscriptionsBaseUrl();
  try {
    const response = await getRequest(url, UNPAGINATED_LIVE_ROWS);
    return supported(response?.data?.items ?? []);
  } catch (e: any) {
    recordLog(LogLevel.Error, `getAllSubscriptions Attempt: ${url}`, 'fail');
    return [];
  }
}

/**
 * Every subscription belonging to one address.
 *
 * The API offers no exact-email filter, only `PartialEmail`, which compiles to
 * `Email.Contains(...)`. Passing a whole address therefore also matches longer ones
 * that contain it -- querying "ops@example.com" returns "devops@example.com" too --
 * so the result is narrowed to exact matches here. Without that this would show
 * another user's subscriptions and offer to delete them.
 *
 * Swallows failures to an empty list: the user detail card renders its own empty
 * state and must not take down the user profile it sits under.
 */
export async function getSubscriptionsForEmail(
  email: string,
): Promise<EmailNotificationSubscriptionDto[]> {
  const url = subscriptionsBaseUrl();
  const term = (email ?? '').trim();
  if (term === '') {
    return [];
  }

  try {
    const response = await getRequest(url, { PartialEmail: term, ...UNPAGINATED_LIVE_ROWS });
    const items: EmailNotificationSubscriptionDto[] = response?.data?.items ?? [];
    return supported(items.filter(item => sameEmail(item.email, term)));
  } catch (e: any) {
    recordLog(LogLevel.Error, `getSubscriptionsForEmail Attempt: ${url}`, 'fail');
    return [];
  }
}

/**
 * Create one subscription row. Throws; the caller decides how to surface it, and
 * needs to, because a save writes several of these and reports on the set.
 *
 * `active` defaults on rather than being left to the backend: the screen has no
 * inactive-on-create affordance, and a row created inactive would silently never
 * send.
 */
export async function createSubscription(
  subscription: Omit<EmailNotificationSubscriptionDto, 'id' | 'url' | 'active'> &
    Partial<Pick<EmailNotificationSubscriptionDto, 'active'>>,
): Promise<EmailNotificationSubscriptionDto> {
  const response = await postRequest(subscriptionsBaseUrl(), { active: true, ...subscription });
  return response?.data;
}

/** Remove a subscription outright. Throws; the caller surfaces it. */
export async function deleteSubscription(id: number): Promise<void> {
  await deleteRequest(`${subscriptionsBaseUrl()}/${id}`);
}

/**
 * Turn one subscription on or off. Sends the whole row back because the endpoint is a
 * full PUT that re-runs ValidateDto, so omitting fields would fail validation rather
 * than leave them alone.
 */
export async function setSubscriptionActive(
  subscription: EmailNotificationSubscriptionDto,
  active: boolean,
): Promise<EmailNotificationSubscriptionDto> {
  const response = await putRequest(`${subscriptionsBaseUrl()}/${subscription.id}`, {
    ...subscription,
    active,
  });
  return response?.data;
}

/* ---------------------------------------------------------------------------
 * Plant options
 *
 * What the Plants dropdown offers. Read across every region rather than within
 * one: the modal groups by region and lets one address span several, so a
 * per-region read would mean one request per region and a dropdown that changes
 * under a region selector the screen does not have.
 * ------------------------------------------------------------------------- */

/** A plant as the dropdown needs it: what to show, and what to save. */
export interface PlantOption {
  key: string;
  name: string;
  region: UrlKeyDto;
}

/**
 * Plants across every region the caller can reach, keyed by url.
 *
 * ValidateDto requires the plant to be in the subscription's region, so the region
 * travels with the plant rather than being chosen separately -- which is also what
 * lets the dropdown group by it.
 *
 * Plants with no url or no region are dropped: neither can be saved, and offering
 * one produces a 400 at submit rather than at selection.
 */
export async function getSubscriptionPlants(): Promise<PlantOption[]> {
  // Best-effort by design, said explicitly because getPlantList propagates (DEV-4427).
  // Both consumers -- EmailNotificationsView.load and UserSubscriptionsCard.load -- await
  // this inside an uncaught Promise.all with isLoading true, so a rejection would strand
  // the screen on a permanent spinner. Degrading to url labels is the old behaviour and
  // the right one here.
  const plants: PlantDto[] = (await getPlantList().catch(() => [])) ?? [];
  return plants
    .filter(plant => !!plant.url && !!plant.region?.url)
    .map(plant => ({
      key: plant.url!,
      name: plant.name ?? '',
      region: plant.region!,
    }));
}

/**
 * A plant option list as a url-to-name map, which is what the row labelling needs.
 * Shared so the admin table's expanded detail and the user profile's card name a plant
 * the same way.
 */
export const plantOptionNames = (options: PlantOption[]): { [url: string]: string } =>
  // Mutated, not spread. `{ ...all }` per option allocates a fresh map for every plant
  // and this runs on a render path -- the map is built here and never shared, so there
  // is nothing for the copying to protect.
  options.reduce<{ [url: string]: string }>((all, option) => {
    all[option.key] = option.name;
    return all;
  }, {});
